import React, { useRef, useState } from 'react';
import {
    Typography, Snackbar, Paper, Grid, Button, TextField, OutlinedInput,
    IconButton, InputAdornment, FormHelperText, FormControl, InputLabel
} from '@mui/material';
import SendIcon from '@mui/icons-material/Login';
import { EurofinsLogo } from 'components/svg/EurofinsLogo';
import { ElvisConnect } from 'components/svg/ElvisConnect';
import { NavLink } from '../home/Home-components';
import { useNavigate } from "react-router";
import MuiAlert, { AlertProps } from '@mui/material/Alert';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import axios from 'axios';

export function Login() {
    let navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState();
    const [open, setOpen] = useState(false);
    const [invalidOTP, setInValidOTP] = useState(false);
    const [emailError, setEmailError] = useState("");
    const [passwordError, setPasswordError] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isSubmittingRef = useRef(false);

    const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        setOpen(false);
    };

    const handleClickShowPassword = () => setShowPassword((show) => !show);

    // Email validation regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const loginUser = (e: any) => {
        e.preventDefault();
        if (isSubmittingRef.current) {
            return;
        }
        let isValid = true;

        // Validate email
        if (!email) {
            setEmailError("Please enter email!");
            isValid = false;
        } else if (!emailRegex.test(email)) {
            setEmailError("Please enter a valid email!");
            isValid = false;
        } else {
            setEmailError("");
        }

        // Validate password
        if (!password) {
            setPasswordError("Please enter password!");
            isValid = false;
        } else {
            setPasswordError("");
        }

        if (isValid) {
            const params = {
                userName: email,
                password: password
            }
            if (email.length > 0 && password.length > 0) {
                isSubmittingRef.current = true;
                setIsSubmitting(true);
                axios.post(`${process.env.REACT_APP_BASE_URL}/authexternalUser`, params,
                    {
                        responseType: 'json',
                        headers: {
                            "content-type": "application/json"
                        }
                    }
                ).then((res: any) => {
                    if (res.status === 200) {
                        navigate(`/verificationOtp`, { state: { emailId: email, accessTokenValue: res.data.token } });
                    } else {
                        isSubmittingRef.current = false;
                        setIsSubmitting(false);
                    }
                }).catch((error: any) => {
                    isSubmittingRef.current = false;
                    setIsSubmitting(false);
                    const status = error?.response?.status;
                    const data = error?.response?.data;
                    const message = typeof data === 'string'
                        ? data
                        : data?.title || data?.message || data?.Message || data?.error || 'Login failed. Please try again.';

                    setError(status ? `${status} - ${message}` : message);
                    setOpen(true);
                    setTimeout(() => { setOpen(false) }, 6000);
                });
            }
        }

    }

    const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
        props,
        ref,
    ) {
        return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
    });

    return (
        <Grid container component="main"
            sx={{
                height: "100vh",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
                backgroundSize: "cover",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
            }}>
            <Grid
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "24px",
                    borderRadius: "6px"
                }}
                item
                xs={12}
                sm={8}
                md={3}
                component={Paper}
                elevation={0}
                square
            >
                <div style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center"
                }}>
                    <div><EurofinsLogo /></div>
                    <div><ElvisConnect /></div>
                    <form style={{ width: "100%" }} data-testid="form" onSubmit={loginUser}>
                        <TextField
                            label="Email"
                            fullWidth
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            error={!!emailError}
                            helperText={emailError}
                        />
                        <FormControl sx={{ width: '100%', mt: 2 }} variant="outlined">
                            <InputLabel htmlFor="outlined-adornment-password" sx={{ fontSize: '12px', '&.Mui-focused': { color: '#003883' } }}>Password</InputLabel>
                            <OutlinedInput
                                id="outlined-adornment-password"
                                label="Password"
                                type={showPassword ? 'text' : 'password'}
                                fullWidth
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                error={!!passwordError}
                                endAdornment={
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={handleClickShowPassword}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                }
                            />
                            <FormHelperText id="outlined-weight-helper-text" sx={{ color: '#d32f2f' }}>{passwordError}</FormHelperText>
                        </FormControl>
                        <Button variant="contained" sx={{ mt: 2, width: '100%' }} endIcon={<SendIcon />} type="submit" disabled={isSubmitting}>
                            Sign in
                        </Button>
                        <div style={{ width: '100%', textAlign: 'center', fontSize: '12px', marginTop: '24px' }}>
                            <NavLink to="/forgotpassword">Forgot Password?</NavLink>
                        </div>
                        <div style={{ width: '100%', textAlign: 'center', marginTop: '24px' }}>
                            <Typography variant="subtitle1" sx={{ fontSize: '14px' }}>
                                If you are Eurofins user please click <NavLink to="/famslogin">here</NavLink>.
                            </Typography>
                        </div>
                    </form>
                    <Typography variant="subtitle1" mt={2}>Copyright © Eurofins {new Date().getFullYear()}</Typography>
                </div>
            </Grid>
            <Snackbar open={open} autoHideDuration={6000} onClose={handleClose}>
                <Alert onClose={handleClose} severity="warning">
                    {error}
                </Alert>
            </Snackbar>
            <Snackbar open={invalidOTP} autoHideDuration={6000} onClose={() => setInValidOTP(false)}>
                <Alert severity="error" onClose={() => setInValidOTP(false)}>
                    Invalid OTP
                </Alert>
            </Snackbar>
        </Grid>
    )
}
